import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import Logo from "./Logo";
import { EASE } from "./Reveal";
import { site, navLinks } from "../config/site";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 32);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <>
      <header className="fixed inset-x-0 top-0 z-50">
        <div
          className={`overflow-hidden bg-ink text-ivory transition-all duration-500 ${
            scrolled ? "max-h-0" : "max-h-12"
          }`}
        >
          <div className="container-x flex h-9 items-center justify-center gap-3 text-[11px] tracking-wide md:text-xs">
            <span className="hidden text-ivory/80 sm:inline">
              {site.announcement.text}
            </span>
            <span className="text-ivory/80 sm:hidden">Call or WhatsApp us today</span>
            <Link
              to="/book"
              data-testid="announcement-book-cta"
              className="font-semibold uppercase tracking-[0.14em] text-champagne transition-colors hover:text-champagne-dark"
            >
              {site.announcement.cta}
            </Link>
          </div>
        </div>

        <div
          className={`transition-all duration-500 ${
            scrolled
              ? "border-b border-line bg-ivory/95 shadow-[0_1px_24px_rgba(23,23,23,0.04)] backdrop-blur-md"
              : "border-b border-transparent bg-transparent"
          }`}
        >
          <div className="container-x flex h-[72px] items-center justify-between">
            <Logo />
            <nav className="hidden items-center gap-7 lg:flex" aria-label="Primary">
              {navLinks.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`nav-${l.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `relative py-2 text-[13px] font-semibold uppercase tracking-[0.14em] transition-colors after:absolute after:bottom-0 after:left-0 after:h-px after:w-full after:origin-left after:bg-champagne after:transition-transform after:duration-300 ${
                      isActive
                        ? "text-ink after:scale-x-100"
                        : "text-soften after:scale-x-0 hover:text-ink hover:after:scale-x-100"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
            </nav>
            <div className="flex items-center gap-3">
              <a
                href={site.phoneHref}
                data-testid="header-call-btn"
                className="hidden h-[48px] w-[48px] items-center justify-center border border-line text-ink transition-colors hover:border-champagne hover:text-champagne md:flex"
                aria-label={`Call the salon on ${site.phoneDisplay}`}
              >
                <Phone className="h-4 w-4" />
              </a>
              <Link
                to="/book"
                data-testid="header-book-btn"
                className="hidden min-h-[48px] items-center bg-ink px-6 text-[13px] font-semibold uppercase tracking-[0.14em] text-white transition-colors duration-300 hover:bg-taupe lg:inline-flex"
              >
                Book an Appointment
              </Link>
              <button
                type="button"
                data-testid="mobile-menu-button"
                onClick={() => setOpen(true)}
                className="flex h-[48px] w-[48px] items-center justify-center border border-line text-ink lg:hidden"
                aria-label="Open menu"
                aria-expanded={open}
              >
                <Menu className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            className="grain-dark fixed inset-0 z-[70] flex flex-col bg-ink text-ivory"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4, ease: EASE }}
            role="dialog"
            aria-modal="true"
            aria-label="Menu"
          >
            <div className="container-x flex h-[72px] items-center justify-between">
              <Logo dark compact />
              <button
                type="button"
                data-testid="mobile-menu-close"
                onClick={() => setOpen(false)}
                className="flex h-[48px] w-[48px] items-center justify-center border border-white/20 text-ivory"
                aria-label="Close menu"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <nav
              className="container-x mt-6 flex flex-1 flex-col gap-1 overflow-y-auto"
              aria-label="Mobile"
            >
              {[...navLinks, { label: "Book an Appointment", to: "/book" }].map(
                (l, i) => (
                  <motion.div
                    key={l.to + l.label}
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.08 + i * 0.06, duration: 0.6, ease: EASE }}
                  >
                    <NavLink
                      to={l.to}
                      data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                      className={({ isActive }) =>
                        `group flex items-baseline gap-4 border-b border-white/10 py-4 ${
                          isActive ? "text-champagne" : "text-ivory"
                        }`
                      }
                    >
                      <span className="font-display text-sm italic text-champagne">
                        0{i + 1}
                      </span>
                      <span className="font-display text-3xl font-medium transition-colors group-hover:text-champagne">
                        {l.label}
                      </span>
                    </NavLink>
                  </motion.div>
                ),
              )}
            </nav>
            <motion.div
              className="container-x pb-10 pt-6"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5, duration: 0.6 }}
            >
              <p className="text-xs uppercase tracking-[0.2em] text-ivory/50">
                {site.hoursDays} · {site.hoursTime}
              </p>
              <a
                href={site.phoneHref}
                data-testid="mobile-menu-call"
                className="mt-2 block font-display text-2xl text-champagne"
              >
                {site.phoneDisplay}
              </a>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
